import { diffChars } from "diff";

type Kind = "same" | "removed" | "added";

type Part = { text: string; kind: Kind };

export type CharDiffPart = Part & { key: string };

const MIN_KEPT_RUN = 3;

const partsOf = (from: string, to: string): Part[] =>
	diffChars(from, to).map((change) => ({
		text: change.value,
		kind: change.added ? "added" : change.removed ? "removed" : "same",
	}));

const absorbShortRuns = (parts: Part[]): Part[] =>
	parts.flatMap((part, index) => {
		const between = index > 0 && index < parts.length - 1;
		if (part.kind !== "same" || !between || part.text.length >= MIN_KEPT_RUN) return [part];
		return [
			{ text: part.text, kind: "removed" },
			{ text: part.text, kind: "added" },
		];
	});

const regroup = (parts: Part[]): Part[] => {
	const result: Part[] = [];
	let removed = "";
	let added = "";
	const flush = () => {
		if (removed) result.push({ text: removed, kind: "removed" });
		if (added) result.push({ text: added, kind: "added" });
		removed = "";
		added = "";
	};
	for (const part of parts) {
		if (part.kind === "removed") removed += part.text;
		else if (part.kind === "added") added += part.text;
		else {
			flush();
			result.push(part);
		}
	}
	flush();
	return result;
};

const withKeys = (parts: Part[]): CharDiffPart[] => {
	let offset = 0;
	return parts.map((part) => {
		const key = `${part.kind}:${offset}`;
		offset += part.text.length;
		return { ...part, key };
	});
};

const charDiff = (from: string, to: string): CharDiffPart[] => withKeys(regroup(absorbShortRuns(partsOf(from, to))));

export default charDiff;
